import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTabPersistence } from '../hooks/admin/useTabPersistence';
import { ArticlesProvider } from '../contexts/ArticlesContext';
import { SourcesProvider } from '../contexts/SourcesContext';
import { SystemProvider, useSystem } from '../contexts/SystemContext';
import { AdminTab } from '../types/enums';
import { OverviewTab } from '../components/admin/OverviewTab';
import { ArticlesTab } from '../components/admin/ArticlesTab';
import { SourcesTab } from '../components/admin/SourcesTab';
import { QueueTab } from '../components/admin/QueueTab';
import { CostsTab } from '../components/admin/CostsTab';
import { ManualTab } from '../components/admin/ManualTab';
import { CategoriesTab } from '../components/admin/CategoriesTab';
import { ActorsTab } from '../components/admin/ActorsTab';
import CountriesTab from '../components/admin/CountriesTab';
import PromptManagementTab from '../components/admin/PromptManagementTab';
import { CacheTab } from '../components/admin/CacheTab';
import EmailManagementTab from '../components/admin/EmailManagementTab';
import SeoTab from '../components/admin/SeoTab';
import { AccountTab } from '../components/admin/AccountTab';
import classNames from 'classnames';

export const AdminDashboard: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { activeTab, setActiveTab, initializeTab, saveTabForRedirect } = useTabPersistence();

  // Initialize tab from URL or localStorage
  useEffect(() => {
    initializeTab();
  }, [initializeTab]);

  // Save current tab before redirect to login
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      saveTabForRedirect(activeTab);
    }
  }, [authLoading, isAuthenticated, activeTab, saveTabForRedirect]);

  if (authLoading) {
    return (
      <div className="min-h-screen tc-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="tc-spinner mb-4"></div>
          <p className="tc-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const tabs = [
    { id: AdminTab.OVERVIEW, label: '📊 Overview', icon: '📊' },
    { id: AdminTab.SOURCES, label: '📡 Sources', icon: '📡' },
    { id: AdminTab.ARTICLES, label: '📄 Articles', icon: '📄' },
    { id: AdminTab.QUEUE, label: '⚙️ Queue', icon: '⚙️' },
    { id: AdminTab.COSTS, label: '💰 Costs', icon: '💰' },
    { id: AdminTab.MANUAL, label: '🔧 Manual', icon: '🔧' },
    { id: AdminTab.CATEGORIES, label: '🏷️ Categories', icon: '🏷️' },
    { id: AdminTab.ACTORS, label: '👥 Actors', icon: '👥' },
    { id: AdminTab.COUNTRIES, label: '🗺️ Countries', icon: '🗺️' },
    { id: AdminTab.PROMPTS, label: '🤖 Prompts', icon: '🤖' },
    { id: AdminTab.CACHE, label: '🗄️ Cache', icon: '🗄️' },
    { id: AdminTab.EMAIL, label: '✉️ Email', icon: '✉️' },
    { id: AdminTab.SEO, label: '🔎 SEO', icon: '🔎' },
    { id: AdminTab.ACCOUNT, label: '👤 Account', icon: '👤' },
  ];

  const SystemPollingController: React.FC = () => {
    const { fetchSystemData, startPolling, stopPolling } = useSystem();

    // Start/stop polling based on active admin tab
    useEffect(() => {
      const shouldPoll = activeTab === AdminTab.OVERVIEW || activeTab === AdminTab.QUEUE;

      if (shouldPoll) {
        fetchSystemData();
        startPolling();
      } else {
        stopPolling();
      }

      return () => {
        stopPolling();
      };
    }, [activeTab, fetchSystemData, startPolling, stopPolling]);

    // Pause polling when tab is hidden; resume when visible and eligible
    useEffect(() => {
      const handleVisibility = () => {
        const shouldPoll = activeTab === AdminTab.OVERVIEW || activeTab === AdminTab.QUEUE;
        if (document.hidden) {
          stopPolling();
        } else if (shouldPoll) {
          startPolling();
        }
      };
      document.addEventListener('visibilitychange', handleVisibility);
      return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [activeTab, startPolling, stopPolling]);

    return null;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case AdminTab.OVERVIEW:
        return (
          <SourcesProvider>
            <OverviewTab />
          </SourcesProvider>
        );
      
      case AdminTab.SOURCES:
        return (
          <SourcesProvider>
            <SourcesTab />
          </SourcesProvider>
        );
      
      case AdminTab.ARTICLES:
        return (
          <SourcesProvider>
            <ArticlesProvider>
              <ArticlesTab />
            </ArticlesProvider>
          </SourcesProvider>
        );
      
      case AdminTab.QUEUE:
        return <QueueTab />;
      
      case AdminTab.COSTS:
        return <CostsTab />;
      
      case AdminTab.MANUAL:
        return <ManualTab />;
      
      case AdminTab.CATEGORIES:
        return <CategoriesTab />;
      
      case AdminTab.ACTORS:
        return <ActorsTab />;
      
      case AdminTab.COUNTRIES:
        return <CountriesTab />;
      
      case AdminTab.PROMPTS:
        return <PromptManagementTab />;
      
      case AdminTab.CACHE:
        return <CacheTab />;
      
      case AdminTab.EMAIL:
        return <EmailManagementTab />;
      case AdminTab.SEO:
        return <SeoTab />;
      case AdminTab.ACCOUNT:
        return <AccountTab />;
      
      default:
        return (
          <div className="tc-card p-6">
            <h3 className="text-xl font-semibold mb-4 tc-text-primary">Page Not Found</h3>
            <p className="tc-text-secondary">The requested tab could not be found.</p>
          </div>
        );
    }
  };

  return (
    <SystemProvider>
    <div className="min-h-screen tc-bg-secondary">
      
      {/* Header */}
      <header className="tc-bg-primary shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <h1 className="text-3xl font-bold tc-text-primary">ThreatCompass Admin</h1>
            </div>
            <SystemPollingController />
            <HeaderRight />
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="tc-bg-primary border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={classNames(
                  'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
                  {
                    'border-blue-500 text-blue-600': activeTab === tab.id,
                    'border-transparent tc-text-secondary hover:tc-text-primary hover:border-gray-300': activeTab !== tab.id,
                  }
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content with single SystemProvider and polling controller */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {renderTabContent()}
        </div>
      </main>
    </div>
    </SystemProvider>
  );
};

const HeaderRight: React.FC = () => {
  const { lastRefreshed, pollingMs } = useSystem();
  const fmt = (d?: Date | null) => (d ? `${d.toLocaleTimeString()}` : '—');
  const cfAnalyticsUrl = process.env.REACT_APP_CF_ANALYTICS_URL;
  return (
    <div className="flex items-center space-x-4">
      <span className="tc-text-tertiary text-xs">Polling: {Math.round(pollingMs / 1000)}s</span>
      <span className="tc-text-tertiary text-xs">Last refresh: {fmt(lastRefreshed)}</span>
      {cfAnalyticsUrl ? (
        <a
          href={cfAnalyticsUrl}
          target="_blank"
          rel="noreferrer"
          className="tc-btn tc-btn-secondary"
          title="Open Cloudflare Web Analytics dashboard"
        >
          Analytics
        </a>
      ) : null}
      <a href="/admin/logout" className="tc-btn tc-btn-secondary">Logout</a>
    </div>
  );
};
